import { pointsToSegments } from '@cutr/constants/windows/presets/resizers';
import { WindowSpec } from '@cutr/constants/windows/windows';

import { useKonva } from './KonvaContext';
import { LineCaption } from './primitives/LineCaption';
import { Box } from './types';

type CaptionsProps = {
  frameWidthMM: number;
  frameX: number;
  windowSpec: WindowSpec;
};

export const BottomCaptions = ({
  frameWidthMM,
  frameX,
  windowSpec,
}: CaptionsProps) => {
  const { renderStyles } = useKonva();

  const { bottomMM: frameLinesBottomMM } =
    renderStyles.frameDimensionLineOffset;

  if (!windowSpec) return null;

  const presetState = windowSpec.meta.presetState;
  const xDividers = presetState.xDividers;

  const widths = pointsToSegments(xDividers, frameWidthMM);
  const widthsCount = widths.length;

  const widthLines = widths.map((width, i) => {
    const sumUpToIndex = widths
      .slice(0, i)
      .reduce((sum, curr) => sum + curr, 0);

    return {
      key: `area-width-${i}-${width}`,
      box: {
        x: frameX + sumUpToIndex,
        y: frameLinesBottomMM,
        width,
        height: frameLinesBottomMM,
      },
    };
  });

  return (
    <>
      <WidthLine
        key={`frame-width-input-${frameWidthMM}`}
        padding={44}
        box={{
          x: frameX,
          y: widthsCount > 1 ? 0 : frameLinesBottomMM,
          width: frameWidthMM,
          height: frameLinesBottomMM,
        }}
      />
      {widthsCount > 1 &&
        widthLines.map((line) => (
          <WidthLine key={line.key} padding={44} box={line.box} />
        ))}
    </>
  );
};

export const LeftCaptions = ({
  frameHeightMM,
  frameY,
  windowSpec,
}: {
  frameHeightMM: number;
  frameY: number;
  windowSpec: WindowSpec;
}) => {
  const { renderStyles } = useKonva();

  const { leftMM: frameLinesLeftMM } = renderStyles.frameDimensionLineOffset;
  const { leftMM: windowLinesLeftMM } = renderStyles.areaDimensionLineOffset;

  if (!windowSpec) return null;

  const presetState = windowSpec.meta.presetState;
  const yDividers = presetState.yDividers;

  const heights = pointsToSegments(yDividers, frameHeightMM);
  const heightsCount = heights.length;

  const heightLines = heights.map((height, i) => {
    const sumUpToIndex = heights
      .slice(0, i)
      .reduce((sum, curr) => sum + curr, 0);

    return {
      key: `area-height-${i}-${height}`,
      index: i,
      box: {
        x: frameLinesLeftMM,
        y: frameY + sumUpToIndex,
        width: windowLinesLeftMM,
        height,
      },
    };
  });

  const frameShape = windowSpec.frameShape;
  const hasShapedTop = frameShape.frameType === 'rectangle-arc-top';
  const lastDivider =
    yDividers.length > 0 ? yDividers[yDividers.length - 1] : 0;

  return (
    <>
      <HeightLine
        padding={60}
        box={{
          x: heightsCount > 1 ? 0 : frameLinesLeftMM,
          y: frameY,
          width: frameLinesLeftMM,
          height: frameHeightMM,
        }}
      />
      {heightsCount > 1 &&
        heightLines
          .filter((line) => !(hasShapedTop && line.index === heightsCount - 1))
          .map((line) => (
            <HeightLine key={line.key} padding={40} box={line.box} />
          ))}
      {frameShape.frameType === 'rectangle-arc-top' && (
        <>
          <HeightLine
            padding={40}
            box={{
              x: frameLinesLeftMM,
              y: frameY + lastDivider,
              width: windowLinesLeftMM,
              height: frameShape.heightMM - lastDivider,
            }}
          />
          <HeightLine
            padding={40}
            box={{
              x: frameLinesLeftMM,
              y: frameY + frameShape.heightMM,
              width: windowLinesLeftMM,
              height: frameShape.heightArcMM,
            }}
          />
        </>
      )}
    </>
  );
};

type LineProps = {
  box: Box;
  padding: number;
  text?: string;
};

export const WidthLine = ({ box, padding, text }: LineProps) => {
  const { x, y } = box;

  const lineBox = {
    ...box,
    x: x,
    y: y + padding * 1.2,
  };

  return (
    <LineCaption
      text={text || `${Math.trunc(box.width)}mm`}
      direction="horizontal"
      box={lineBox}
      options={{
        altTriangle: true,
      }}
    />
  );
};

export const HeightLine = ({ box, padding, text }: LineProps) => {
  const { x, y } = box;

  const lineBox = {
    ...box,
    x: x + padding * 1.2,
    y: y,
  };

  return (
    <LineCaption
      text={text || `${Math.trunc(box.height)}mm`}
      direction="vertical"
      box={lineBox}
      options={{
        altTriangle: true,
      }}
    />
  );
};
